import React, { useContext } from 'react';
import { UserLocationEntryModal } from '@honda-canada/design-system-react';
import { UserLocationContext } from '@honda-canada/user-location';
import { compiler } from 'markdown-to-jsx';
import styled from 'styled-components';
import css from '@styled-system/css';
import { useTranslation } from 'react-i18next';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { getDefaultLocationParamsForProvince, useConfiguration } from './utils';
import useAppName from '../../utils/sitecoreContext/useAppName';
import { ACURA_SITE_NAME, HONDA_SITE_NAME, PE_SITE_NAME, PSP_SITE_NAME } from '../../utils/constants';
import { variant1, variant2, variant3 } from '../Footer/service/constants';

const BodyText = styled('span')(({ bodyTextLinkStyling }) =>
  css({
    a: {
      color: 'primary',
      textDecoration: 'none',
      fontFamily: 'bold',
      ...bodyTextLinkStyling,
    },
  }),
);

const UserLocationModal = ({
  onLocationDetected,
  onSubmitLocation,
  bodyTextLinkStyling,
  setIsUserLocationModalOpen,
  ...rest
}) => {
  const { t } = useTranslation();
  const {
    detectUserLocationFromBrowser,
    getAutocompletePredictions,
    isFetchingUserLocation,
    establishUserLocationFromAutocompleteSearch,
  } = useContext(UserLocationContext);
  const { sitecoreContext: { pageEditing, settings } = {} } = useSitecoreContext() || {};
  const userLocation = useContext(UserLocationContext);
  const defaultProvince = settings?.defaultProvince;

  const appName = useAppName();

  let variant;

  switch (appName) {
    case ACURA_SITE_NAME:
      variant = variant1;
      break;
    case HONDA_SITE_NAME:
      variant = variant1;
      break;
    case PE_SITE_NAME:
      variant = variant3;
      break;
    case PSP_SITE_NAME:
      variant = variant2;
      break;
    default:
      break;
  }

  const { submitButtonStyling } = useConfiguration(variant);

  const handleOnClose = () => {
    if (!userLocation?.addressSelectedByUser) {
      if (!userLocation?.provinceCode || userLocation?.provinceCode !== defaultProvince) {
        userLocation?.setLocation(getDefaultLocationParamsForProvince(defaultProvince));
      }
    }
    setIsUserLocationModalOpen(false);
  };

  if (pageEditing) return null;

  return (
    <UserLocationEntryModal
      ariaLabel={t('Shared.UserLocation.modalHeading')}
      closeButtonAriaLabel={t('Shared.UserLocation.modalCloseButtonAria')}
      titleText={t('Shared.UserLocation.modalHeading')}
      bodyText={
        <BodyText bodyTextLinkStyling={bodyTextLinkStyling}>
          {compiler(t('Shared.UserLocation.modalBodyText'))}
        </BodyText>
      }
      enterLocationLabel={t('Shared.UserLocation.modalEnterLocationLabel')}
      enterLocationAriaLabel={t('Shared.UserLocation.modalEnterLocationLabel')}
      submitButtonText={t('Shared.UserLocation.modalSubmitButton')}
      submitButtonStyling={submitButtonStyling}
      useMyLocationText={t('Shared.UserLocation.modalUseMyLocationButton')}
      inputPlaceholderText={t('Shared.UserLocation.modalInputPlaceholder')}
      isFetchingUserLocation={isFetchingUserLocation}
      getAutocompletePredictions={getAutocompletePredictions}
      onDetectLocation={async () => {
        await detectUserLocationFromBrowser(onLocationDetected);
      }}
      onSubmitLocation={locationSearchValue => {
        establishUserLocationFromAutocompleteSearch(locationSearchValue, onLocationDetected);
        if (onSubmitLocation) onSubmitLocation();
      }}
      onClosed={handleOnClose}
      {...rest}
    />
  );
};

export default UserLocationModal;
